import React from "react";
import i18next from "i18next";
import classnames from "classnames";
import { ResponsiveImage, Sizes, SrcSet } from "../ResponsiveImage.component";
import { SocialNetworkData } from "../../lib/social";
import { MagicLink } from "../../ui";
import "./LiveCard.style.scss";
import { EqualizerIcon } from "../icons/EqualizerIcon.component";
import { placeholderImage } from "../../lib/image";

export enum LiveCardType {
    RECENT = "recent",
    TOPSONG = "topsong",
    MICROSITE = "microsite",
    ONAIR = "onair",
}

export type ImageSource = "station" | "show" | "other";

export interface LiveCardImage {
    url: string | null | undefined;
    source: ImageSource;
}

export interface LiveCardProps {
    image: LiveCardImage;
    title: string;
    content?: JSX.Element | null;
    url?: string;
    alt?: string;
    social?: SocialNetworkData[];
    className?: string;
    type?: LiveCardType;
    equalizer?: boolean;
    rank?: number;
    h1?: boolean;
    target?: string;
}

interface ThumbProps {
    imgUrl: string;
    source: ImageSource;
    type: string;
    url?: string;
    alt?: string;
    target?: string;
}

interface TitleProps {
    title: string;
    type: string;
    url?: string;
    h1?: boolean;
    target?: string;
}

const imgQuality = 75;

const Title = ({ title, type = "microsite", url = "", h1 = true, target = "_blank" }: TitleProps) => {
    let heading = null;
    switch (type) {
        case LiveCardType.ONAIR:
        case LiveCardType.RECENT:
        case LiveCardType.TOPSONG:
            heading = (
                <h2 className="livecard-title">
                    {url ? (
                        <MagicLink to={url} target={target}>
                            {title}
                        </MagicLink>
                    ) : (
                        title
                    )}
                </h2>
            );
            break;
        case LiveCardType.MICROSITE:
        default:
            heading = (
                <React.Fragment>
                    {React.createElement(
                        h1 ? "h1" : "h2",
                        { className: "livecard-title" },
                        url ? (
                            <MagicLink to={url} target={target}>
                                {title}
                            </MagicLink>
                        ) : (
                            title
                        ),
                    )}
                </React.Fragment>
            );
            break;
    }
    return heading;
};

export const Thumb = ({ imgUrl, source, type, url = "", alt = "", target }: ThumbProps) => {
    let srcSet: SrcSet[] = [];
    let sizes: Sizes[] = [];
    let placeholderSrcset: SrcSet[] = [];
    let imgOps = `?ops=gravity("center"),ratio(1,1),fit(190,190),quality(${imgQuality})`;
    switch (type) {
        case LiveCardType.ONAIR:
            // I hate everything about this, but it's the best I could come up with for this problem
            if (source === "station") {
                imgOps = `?ops=gravity("center"),contain(190,190),quality(${imgQuality})`;
                srcSet = [
                    {
                        url: `${imgUrl}?ops=gravity("center"),contain(190,190),quality(${imgQuality})`,
                        descriptor: "190w",
                    },
                    {
                        url: `${imgUrl}?ops=gravity("center"),contain(360,360),quality(${imgQuality})`,
                        descriptor: "360w",
                    },
                    {
                        url: `${imgUrl}?ops=gravity("center"),contain(225,225),quality(${imgQuality})`,
                        descriptor: "225w",
                    },
                    {
                        url: `${imgUrl}?ops=gravity("center"),contain(170,170),quality(${imgQuality})`,
                        descriptor: "170w",
                    },
                    {
                        url: `${imgUrl}?ops=gravity("center"),contain(300,300),quality(${imgQuality})`,
                        descriptor: "300w",
                    },
                ];
                sizes = [
                    {
                        media: "(max-width: 420px)",
                        size: "190px",
                    },
                    {
                        media: "(max-width: 768px)",
                        size: "360px",
                    },
                    {
                        media: "(max-width: 1060px)",
                        size: "225px",
                    },
                    {
                        media: "(max-width: 1440px)",
                        size: "170px",
                    },
                    {
                        media: "(min-width: 1441px)",
                        size: "300px",
                    },
                    {
                        media: "auto",
                        size: "",
                    },
                ];
                placeholderSrcset = [
                    {
                        url: `${placeholderImage}?ops=gravity("center"),contain(190,190),quality(${imgQuality})`,
                        descriptor: "190w",
                    },
                    {
                        url: `${placeholderImage}?ops=gravity("center"),contain(360,360),quality(${imgQuality})`,
                        descriptor: "360w",
                    },
                    {
                        url: `${placeholderImage}?ops=gravity("center"),contain(225,225),quality(${imgQuality})`,
                        descriptor: "225w",
                    },
                    {
                        url: `${placeholderImage}?ops=gravity("center"),contain(170,170),quality(${imgQuality})`,
                        descriptor: "170w",
                    },
                    {
                        url: `${placeholderImage}?ops=gravity("center"),contain(300,300),quality(${imgQuality})`,
                        descriptor: "300w",
                    },
                ];
                break;
            }

            imgOps = `?ops=gravity("center"),ratio(1,1),fit(190,190),quality(${imgQuality})`;
            srcSet = [
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(190,190),quality(${imgQuality})`,
                    descriptor: "190w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(360,360),quality(${imgQuality})`,
                    descriptor: "360w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(225,225),quality(${imgQuality})`,
                    descriptor: "225w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(170,170),quality(${imgQuality})`,
                    descriptor: "170w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(300,300),quality(${imgQuality})`,
                    descriptor: "300w",
                },
            ];
            sizes = [
                {
                    media: "(max-width: 420px)",
                    size: "190px",
                },
                {
                    media: "(max-width: 768px)",
                    size: "360px",
                },
                {
                    media: "(max-width: 1060px)",
                    size: "225px",
                },
                {
                    media: "(max-width: 1440px)",
                    size: "170px",
                },
                {
                    media: "(min-width: 1441px)",
                    size: "300px",
                },
                {
                    media: "auto",
                    size: "",
                },
            ];
            placeholderSrcset = [
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(190,190),quality(${imgQuality})`,
                    descriptor: "190w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(360,360),quality(${imgQuality})`,
                    descriptor: "360w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(225,225),quality(${imgQuality})`,
                    descriptor: "225w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(170,170),quality(${imgQuality})`,
                    descriptor: "170w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(300,300),quality(${imgQuality})`,
                    descriptor: "300w",
                },
            ];

            break;
        case LiveCardType.RECENT:
        case LiveCardType.TOPSONG:
            srcSet = [
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(190,190),quality(${imgQuality})`,
                    descriptor: "190w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(360,360),quality(${imgQuality})`,
                    descriptor: "360w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(225,225),quality(${imgQuality})`,
                    descriptor: "225w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(170,170),quality(${imgQuality})`,
                    descriptor: "170w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(300,300),quality(${imgQuality})`,
                    descriptor: "300w",
                },
            ];
            sizes = [
                {
                    media: "(max-width: 420px)",
                    size: "190px",
                },
                {
                    media: "(max-width: 768px)",
                    size: "360px",
                },
                {
                    media: "(max-width: 1060px)",
                    size: "225px",
                },
                {
                    media: "(max-width: 1440px)",
                    size: "170px",
                },
                {
                    media: "(min-width: 1441px)",
                    size: "300px",
                },
                {
                    media: "auto",
                    size: "",
                },
            ];
            placeholderSrcset = [
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(190,190),quality(${imgQuality})`,
                    descriptor: "190w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(360,360),quality(${imgQuality})`,
                    descriptor: "360w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(225,225),quality(${imgQuality})`,
                    descriptor: "225w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(170,170),quality(${imgQuality})`,
                    descriptor: "170w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(300,300),quality(${imgQuality})`,
                    descriptor: "300w",
                },
            ];
            break;
        case LiveCardType.MICROSITE:
        default:
            srcSet = [
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(100,100),quality(${imgQuality})`,
                    descriptor: "100w",
                },
                {
                    url: `${imgUrl}?ops=gravity("center"),ratio(1,1),fit(200,200),quality(${imgQuality})`,
                    descriptor: "200w",
                },
            ];
            sizes = [
                {
                    media: "(max-width: 767px)",
                    size: "100px",
                },
                {
                    media: "(min-width: 768px)",
                    size: "200px",
                },
                {
                    media: "auto",
                    size: "",
                },
            ];
            placeholderSrcset = [
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(100,100),quality(${imgQuality})`,
                    descriptor: "100w",
                },
                {
                    url: `${placeholderImage}?ops=gravity("center"),ratio(1,1),fit(200,200),quality(${imgQuality})`,
                    descriptor: "200w",
                },
            ];
            break;
    }
    const placeHolder = `${placeholderImage}${imgOps}`;
    if (url) {
        return (
            <MagicLink to={url} target={target}>
                <ResponsiveImage
                    src={`${imgUrl}${imgOps}`}
                    srcset={srcSet}
                    sizes={sizes}
                    alt={alt}
                    placeholder={placeHolder}
                    placeholderSrcset={placeholderSrcset}
                    initialWidth={100}
                    initialHeight={100}
                />
            </MagicLink>
        );
    }
    return (
        <ResponsiveImage
            src={`${imgUrl}${imgOps}`}
            srcset={srcSet}
            sizes={sizes}
            alt={alt}
            placeholder={placeHolder}
            placeholderSrcset={placeholderSrcset}
            initialWidth={100}
            initialHeight={100}
        />
    );
};

const Equalizer = ({ text = i18next.t("equalizer_text") }: { text: string }) => (
    <section className="equalizer">
        <p>
            <EqualizerIcon />
            <span>{text}</span>
        </p>
    </section>
);

export const LiveCard = (props: LiveCardProps) => {
    const cardClass = classnames("component-live-card", {
        [`${props.className}`]: props.className,
        [`type-${props.type}`]: props.type,
        "with-thumb": props.image.url,
    });

    let eqText = i18next.t("equalizer_text");
    if (props.type === LiveCardType.RECENT) {
        eqText = i18next.t("now_playing");
    }

    const { url: thumbUrl, source: thumbSource } = props.image;
    const alt = props.alt || props.title;
    const { h1 = true } = props;

    if (!thumbUrl) {
        return (
            <div className={cardClass}>
                <Title
                    title={props.title}
                    h1={h1}
                    type={props.type || LiveCardType.MICROSITE}
                    url={props.url}
                    target={props.target}
                />
                {props.content ? <section className="content">{props.content}</section> : null}
            </div>
        );
    }

    return (
        <figure className={cardClass}>
            {props.equalizer ? <Equalizer text={eqText} /> : null}
            {props.image?.url ? (
                <section className="thumb" data-rank={props.rank}>
                    <Thumb
                        imgUrl={thumbUrl || ""}
                        source={thumbSource}
                        type={props.type || LiveCardType.MICROSITE}
                        url={props.url}
                        alt={alt}
                        target={props.target}
                    />
                </section>
            ) : null}
            <figcaption>
                {props.rank ? <h2 className="song-rank">{props.rank}</h2> : null}
                <Title
                    title={props.title}
                    h1={h1}
                    type={props.type || LiveCardType.MICROSITE}
                    url={props.url}
                    target={props.target}
                />
                {props.content ? <section className="content">{props.content}</section> : null}
            </figcaption>
        </figure>
    );
};
